import { pdkAxios } from '@/lib/pdk-axios';
import { db } from '@/models/db';
import { appraisalOfferStoreV2 } from '@/models/stores/appraisal-offers-v2.ts';
import { appraisalStoreV2 } from '@/models/stores/appraisals-v2.ts';
import { instantOfferStoreV2 } from '@/models/stores/instant-offers-v2.ts';
import {
  sellerStore,
  sellerStoreHydrationDelegator,
} from '@/models/stores/sellers.ts';
import { taskStoreV2, taskTemplateStore } from '@/models/stores/tasks-v2.ts';
import type {
  AppraisalOfferV2 as AppraisalOfferV2Type,
  AppraisalV2 as AppraisalV2Type,
  AssignedTask as AssignedTaskType,
  Auction as AuctionType,
  Audit as AuditType,
  Comment as CommentType,
  ConfirmationHistoryEntry as ConfirmationHistoryType,
  Confirmation as ConfirmationType,
  CoreDeal as CoreDealType,
  Address as CustomerAddressType,
  Customer as CustomerType,
  EmojiReaction as EmojiReactionType,
  Favorite as FavoriteType,
  FileMetadata as FileMetadataType,
  Hub as HubType,
  InstantOfferV2 as InstantOfferV2Type,
  Lender as LenderType,
  Notification as NotificationType,
  Payment as PaymentType,
  PriceBreakdown as PriceBreakdownType,
  ProtectionPlanName as ProtectionPlanNameType,
  ProtectionPlanTermLength as ProtectionPlanTermLengthType,
  ProtectionPlanTermMiles as ProtectionPlanTermMilesType,
  ProtectionPlan as ProtectionPlanType,
  ProtectionPlanType as ProtectionPlanTypeType,
  Reply as ReplyType,
  Seller as SellerType,
  SMSAgentMessage as SMSAgentMessageType,
  TaskTemplate as TaskTemplateType,
  Task as TaskType,
  TaskV2 as TaskTypeV2,
  Trade as TradeType,
  User as UserType,
  VehicleListing as VehicleListingType,
  VehicleLocation as VehicleLocationType,
} from '@/models/types';

import {
  AppraisalOfferV2,
  AppraisalV2,
  AssignedTask,
  Auction,
  Audit,
  Comment,
  Confirmation,
  ConfirmationHistory,
  CustomerAddress,
  Deal,
  EmojiReaction,
  Favorite,
  FileMetadata,
  Hub,
  InstantOfferV2,
  Lender,
  Notification,
  Payment,
  PriceBreakdown,
  ProtectionPlan,
  Reply,
  SMSAgentMessage,
  Task,
  TaskTemplate,
  TaskV2,
  Trade,
  User,
  VehicleLocation,
} from './registry';
import type { BaseStore, StoreHydrationDelegator } from './stores/base';
import {
  assignedTaskStore,
  auctionStore,
  auditStore,
  commentStore,
  confirmationHistoryStore,
  confirmationsStore,
  customerAddressStore,
  customerStoreHydrationDelegator,
  dealStore,
  emojiReactionsStore,
  favoriteStore,
  fileMetadataStore,
  hubStore,
  lenderStore,
  notificationStore,
  paymentStore,
  priceBreakdownStore,
  protectionPlanStore,
  replyStore,
  smsAgentMessageStore,
  taskStore,
  tradeStore,
  userStore,
  vehicleLocationStore,
  vehiclesAndListingsFromIndexerHydrationDelegator,
} from './stores/index';
import { protectionPlanNameStoreHydrationDelegator } from './stores/protection-plan-names';
import { protectionPlanTermLengthStoreHydrationDelegator } from './stores/protection-plan-term-lengths';
import { protectionPlanTermMilesStoreHydrationDelegator } from './stores/protection-plan-term-miles';
import { protectionPlanTypeStoreHydrationDelegator } from './stores/protection-plan-types';

export type FullHydrateResponse<T> = {
  data: T[];
  firstSyncId: number;
  lastSyncId: number;
};

export interface DeltaPacketRecord<T> {
  type: 'delta';
  entity: string;
  sync_id: number;
  record_id: string;
  action: 'insert' | 'update' | 'delete' | 'archive' | 'unarchive' | 'create';
  data: T;
  is_v2?: boolean;
}

export interface SyncResponse<T> {
  entries: DeltaPacketRecord<T>[];
  firstSyncId: number;
  lastSyncId: number;
}

type HydrationConfig<Model, T> = {
  fullHydrateEndpoint: string;
  deltaSyncEndpoint: string;
  entity: string;
  dbTable: any;
  syncKeyOverride?: string;
  modelStore: BaseStore<Model, T>;
  hydrationDelegator?: StoreHydrationDelegator<T>;
  shouldApplyDelta?: (delta: DeltaPacketRecord<T>) => Promise<boolean>;
};

type HydrationDelegatorConfig<T> = {
  hydrationKey: string;
  fullHydrateEndpoint: string;
  deltaSyncEndpoint: string | null;
  hydrationDelegator: StoreHydrationDelegator<T>;
};

const CANCELLED_DEALS_SYNC_KEY = 'core_deals_cancelled';
const LEGACY_CANCELLED_DEALS_CLEANUP_KEY =
  'paddock.core_deals.cancelled_cleanup.v1';

// Entity-specific configurations
const hydrationConfigs = {
  hubs: {
    fullHydrateEndpoint: '/hydrate/v2/hubs',
    deltaSyncEndpoint: '/hydrate/deltas/hubs',
    entity: 'hubs',
    dbTable: db.hubs,
    modelStore: hubStore,
  },
  deals: {
    fullHydrateEndpoint: '/hydrate/v2/deals',
    deltaSyncEndpoint: '/hydrate/deltas/deals',
    entity: 'deals',
    dbTable: db.core_deals,
    syncKeyOverride: 'core_deals',
    modelStore: dealStore,
    shouldApplyDelta: shouldApplyDealDelta,
  },
  users: {
    fullHydrateEndpoint: '/hydrate/v2/users',
    deltaSyncEndpoint: '/hydrate/deltas/users',
    entity: 'users',
    dbTable: db.users,
    modelStore: userStore,
  },
  emojiReactions: {
    fullHydrateEndpoint: '/hydrate/v2/emoji-reactions',
    deltaSyncEndpoint: 'hydrate/deltas/emoji_reactions',
    entity: 'emoji_reactions',
    dbTable: db.emoji_reactions,
    modelStore: emojiReactionsStore,
  },
  confirmations: {
    fullHydrateEndpoint: '/hydrate/v2/confirmations',
    deltaSyncEndpoint: 'hydrate/deltas/confirmations',
    entity: 'confirmations',
    dbTable: db.confirmations,
    modelStore: confirmationsStore,
  },
  confirmation_history_entries: {
    fullHydrateEndpoint: '/hydrate/v2/confirmation_history_entries',
    deltaSyncEndpoint: 'hydrate/deltas/confirmation_history_entries',
    entity: 'confirmations_history_entries',
    dbTable: db.confirmation_history_entries,
    modelStore: confirmationHistoryStore,
  },
  comments: {
    fullHydrateEndpoint: '/hydrate/v3/comments',
    deltaSyncEndpoint: '/hydrate/deltas/comments',
    entity: 'comments',
    dbTable: db.comments,
    modelStore: commentStore,
  },
  replies: {
    fullHydrateEndpoint: '/hydrate/v3/replies',
    deltaSyncEndpoint: '/hydrate/deltas/comment_replies',
    entity: 'comment_replies',
    dbTable: db.comment_replies,
    modelStore: replyStore,
  },
  payments: {
    fullHydrateEndpoint: '/hydrate/v2/payments',
    deltaSyncEndpoint: '/hydrate/deltas/deal_payments',
    entity: 'deal_payments',
    dbTable: db.deal_payments,
    modelStore: paymentStore,
  },
  files: {
    fullHydrateEndpoint: '/hydrate/v2/files',
    deltaSyncEndpoint: '/hydrate/deltas/deal_files',
    entity: 'deal_files',
    dbTable: db.deal_files,
    modelStore: fileMetadataStore,
  },
  customerAddresses: {
    fullHydrateEndpoint: '/hydrate/v2/customer-addresses',
    deltaSyncEndpoint: '/hydrate/deltas/customer_addresses',
    entity: 'customer_addresses',
    syncKeyOverride: 'deal_customer_addresses',
    dbTable: db.deal_customer_addresses,
    modelStore: customerAddressStore,
  },
  lenders: {
    fullHydrateEndpoint: '/hydrate/v2/lenders',
    deltaSyncEndpoint: '/hydrate/deltas/lenders',
    entity: 'lenders',
    dbTable: db.lenders,
    modelStore: lenderStore,
  },
  auctions: {
    fullHydrateEndpoint: '/hydrate/v2/auctions',
    deltaSyncEndpoint: '/hydrate/deltas/auctions',
    entity: 'auctions',
    dbTable: db.auctions,
    modelStore: auctionStore,
  },
  vehicleLocations: {
    fullHydrateEndpoint: '/hydrate/v2/vehicle-locations',
    deltaSyncEndpoint: '/hydrate/deltas/vehicle_locations',
    entity: 'vehicle_locations',
    dbTable: db.vehicle_locations,
    modelStore: vehicleLocationStore,
  },
  taskTemplates: {
    fullHydrateEndpoint: '/hydrate/v2/task-templates',
    deltaSyncEndpoint: '/hydrate/deltas/task_templates',
    entity: 'task_templates',
    dbTable: db.task_templates,
    modelStore: taskTemplateStore,
  },
  tasksV2: {
    fullHydrateEndpoint: 'hydrate/v3/tasks',
    deltaSyncEndpoint: 'hydrate/deltas/tasks',
    entity: 'tasks',
    dbTable: db.tasks,
    modelStore: taskStoreV2,
  },
  tasks: {
    fullHydrateEndpoint: '/hydrate/v2/tasks',
    deltaSyncEndpoint: '/hydrate/deltas/deal_tasks',
    entity: 'deal_tasks',
    dbTable: db.deal_tasks,
    modelStore: taskStore,
  },
  assignedTasks: {
    fullHydrateEndpoint: '/hydrate/v2/task-assignments',
    deltaSyncEndpoint: '/hydrate/deltas/deal_task_assignments',
    entity: 'deal_task_assignments',
    dbTable: db.deal_task_assignments,
    modelStore: assignedTaskStore,
  },
  audits: {
    fullHydrateEndpoint: '/hydrate/v2/audits',
    deltaSyncEndpoint: '/hydrate/deltas/deal_audits',
    entity: 'deal_audits',
    dbTable: db.deal_audits,
    modelStore: auditStore,
  },
  notifications: {
    fullHydrateEndpoint: '/hydrate/v2/notifications',
    deltaSyncEndpoint: '/hydrate/deltas/deal_notifications',
    entity: 'deal_notifications',
    dbTable: db.notifications,
    syncKeyOverride: 'notifications',
    modelStore: notificationStore,
  },
  protectionPlans: {
    fullHydrateEndpoint: '/hydrate/v2/protection-plans',
    deltaSyncEndpoint: '/hydrate/deltas/protection_plans',
    entity: 'protection_plans',
    dbTable: db.deal_protection_plans,
    syncKeyOverride: 'deal_protection_plans',
    modelStore: protectionPlanStore,
  },
  priceBreakdowns: {
    fullHydrateEndpoint: '/hydrate/v2/price-breakdowns',
    deltaSyncEndpoint: '/hydrate/deltas/deal_fee_breakdowns',
    entity: 'price_breakdowns',
    dbTable: db.deal_price_breakdowns,
    syncKeyOverride: 'deal_price_breakdowns',
    modelStore: priceBreakdownStore,
  },
  favoriteDeals: {
    fullHydrateEndpoint: '/hydrate/v2/favorite-deals',
    deltaSyncEndpoint: '/hydrate/deltas/favorite_deals',
    entity: 'favorite_deals',
    dbTable: db.favorites,
    syncKeyOverride: 'favorites',
    modelStore: favoriteStore,
  },
  trades: {
    fullHydrateEndpoint: '/hydrate/v2/trades',
    deltaSyncEndpoint: '/hydrate/deltas/trades',
    entity: 'trades',
    dbTable: db.trades,
    modelStore: tradeStore,
  },
  smsAgentMessages: {
    fullHydrateEndpoint: '/hydrate/v2/sms-agent-messages',
    deltaSyncEndpoint: '/hydrate/deltas/sms_agent_messages',
    entity: 'sms_agent_messages',
    dbTable: db.sms_agent_messages,
    modelStore: smsAgentMessageStore,
  },
  appraisalsV2: {
    fullHydrateEndpoint: '/hydrate/v2/appraisals-v2',
    deltaSyncEndpoint: '/hydrate/deltas/appraisals_v2',
    entity: 'appraisals_v2', // V2 entity name with _v2 suffix
    dbTable: db.appraisals_v2,
    modelStore: appraisalStoreV2,
  },
  appraisalOffersV2: {
    fullHydrateEndpoint: '/hydrate/v2/offers-v2',
    deltaSyncEndpoint: '/hydrate/deltas/offers_v2',
    entity: 'offers_v2', // V2 entity name with _v2 suffix
    dbTable: db.appraisal_offers_v2,
    modelStore: appraisalOfferStoreV2,
  },
  instantOffersV2: {
    fullHydrateEndpoint: '/hydrate/v2/instant-offers-v2',
    deltaSyncEndpoint: '/hydrate/deltas/instant_offers_v2',
    entity: 'instant_offers_v2', // V2 entity name with _v2 suffix
    dbTable: db.instant_offers_v2,
    modelStore: instantOfferStoreV2,
  },
  sellers: {
    fullHydrateEndpoint: '/hydrate/v2/sellers',
    deltaSyncEndpoint: '/hydrate/deltas/sellers',
    entity: 'sellers',
    dbTable: db.sellers,
    modelStore: sellerStore,
  },
} as const;

const hydrationDelegatorConfigs = {
  customers: {
    hydrationKey: 'deal_customers',
    fullHydrateEndpoint: '/hydrate/v2/customers',
    deltaSyncEndpoint: '/hydrate/deltas/customers',
    hydrationDelegator: customerStoreHydrationDelegator,
  },
  vehicleListings: {
    hydrationKey: 'vehicle_inventory',
    fullHydrateEndpoint: '/hydrate/v2/inventory',
    deltaSyncEndpoint: '/hydrate/v2/inventory',
    hydrationDelegator: vehiclesAndListingsFromIndexerHydrationDelegator,
  },
  sellers: {
    hydrationKey: 'sellers',
    fullHydrateEndpoint: '/hydrate/v2/sellers',
    deltaSyncEndpoint: null,
    hydrationDelegator: sellerStoreHydrationDelegator,
  },
  protectionPlanNames: {
    hydrationKey: 'protection_plan_names',
    fullHydrateEndpoint: '/hydrate/v2/protection-plan-names',
    deltaSyncEndpoint: null,
    hydrationDelegator: protectionPlanNameStoreHydrationDelegator,
  },
  protectionPlanTypes: {
    hydrationKey: 'protection_plan_types',
    fullHydrateEndpoint: '/hydrate/v2/protection-plan-types',
    deltaSyncEndpoint: null,
    hydrationDelegator: protectionPlanTypeStoreHydrationDelegator,
  },
  protectionPlanTermLengths: {
    hydrationKey: 'protection_plan_term_lengths',
    fullHydrateEndpoint: '/hydrate/v2/protection-plan-term-lengths',
    deltaSyncEndpoint: null,
    hydrationDelegator: protectionPlanTermLengthStoreHydrationDelegator,
  },
  protectionPlanTermMiles: {
    hydrationKey: 'protection_plan_term_miles',
    fullHydrateEndpoint: '/hydrate/v2/protection-plan-term-miles',
    deltaSyncEndpoint: null,
    hydrationDelegator: protectionPlanTermMilesStoreHydrationDelegator,
  },
} as const;

async function shouldApplyDealDelta(
  delta: DeltaPacketRecord<CoreDealType>,
): Promise<boolean> {
  if (delta.action === 'delete' || delta.action === 'archive') {
    return true;
  }

  if (delta.data?.status !== 'cancelled') {
    return true;
  }

  return !!(await db.core_deals.get(delta.record_id));
}

async function cleanupLegacyCancelledDealsFromCoreDeals(): Promise<void> {
  if (localStorage.getItem(LEGACY_CANCELLED_DEALS_CLEANUP_KEY)) {
    return;
  }

  const cancelledDealIds = (await db.core_deals.toArray())
    .filter((deal) => deal.status === 'cancelled')
    .map((deal) => deal.id);

  if (cancelledDealIds.length > 0) {
    await db.core_deals.bulkDelete(cancelledDealIds);
  }

  localStorage.setItem(LEGACY_CANCELLED_DEALS_CLEANUP_KEY, 'true');
}

const shouldLogHydrationTiming = import.meta.env.DEV;
const HYDRATION_LEDGER = new Map<string, Promise<void>>();

const timeHydration = async <T>(
  key: string,
  hydrate: () => Promise<T>,
): Promise<T> => {
  const label = `hydrate:${key}`;
  if (shouldLogHydrationTiming) {
    console.time(label);
  }

  try {
    return await hydrate();
  } finally {
    if (shouldLogHydrationTiming) {
      console.timeEnd(label);
    }
  }
};

const trackHydration = (
  key: string,
  hydrate: () => Promise<void>,
): Promise<void> => {
  const existingHydration = HYDRATION_LEDGER.get(key);
  if (existingHydration) {
    return existingHydration;
  }

  const hydration = timeHydration(key, hydrate).catch((error) => {
    HYDRATION_LEDGER.delete(key);
    throw error;
  });
  HYDRATION_LEDGER.set(key, hydration);
  return hydration;
};

export async function hydrateEntityV3<Model, T>(
  config: HydrationConfig<Model, T>,
  hydrationDelegatorConfig?: HydrationDelegatorConfig<T>,
): Promise<void> {
  if (hydrationDelegatorConfig) {
    return hydrateEntityViaHydrationDelegator(hydrationDelegatorConfig);
  }

  const key = config.syncKeyOverride || config.entity;
  return trackHydration(key, async () => {
    const checkpoint = await db.syncCheck.get(key);

    // 1) Re‑bootstrap if behind since there's no checkpoint
    if (!checkpoint) {
      await fullHydrate(config);
      return;
    }

    // 2) Delta sync since we have a checkpoint
    const url = `${config.deltaSyncEndpoint}?sinceSyncId=${checkpoint.lastSyncId}`;

    // HTTP snapshot + delta
    const { entries, firstSyncId, lastSyncId } = (
      await pdkAxios.get<SyncResponse<T>>(url, {
        timeout: 60000, // 60 seconds for delta sync endpoints
      })
    ).data;

    // 3) If we're behind, do a full hydrate
    if (checkpoint.lastSyncId && checkpoint.lastSyncId < firstSyncId) {
      await fullHydrate(config);
      return;
    }

    // 4) Apply deltas
    await db.transaction('rw', config.dbTable, db.syncCheck, async () => {
      for (const delta of entries) {
        const { action, data, record_id } = delta;
        if (
          config.shouldApplyDelta &&
          !(await config.shouldApplyDelta(delta))
        ) {
          continue;
        }

        if (action === 'delete' || action === 'archive') {
          try {
            await config.dbTable.delete(record_id);
          } catch (error) {
            console.log('Record', action, data, record_id);
            console.error('Error deleting record', error);
          }
        } else if (action === 'update') {
          try {
            const updated = await config.dbTable.update(record_id, data);
            if (updated === 0) {
              console.warn(`update failed, record ${record_id} not found`);
            }
          } catch (error) {
            console.error('error updating record', error);
          }
        } else {
          try {
            await config.dbTable.put(data, record_id);
          } catch (error) {
            console.error('Error putting record', error);
          }
        }
      }
      if (lastSyncId > (checkpoint.lastSyncId || 0)) {
        try {
          await db.syncCheck.put({ collection: key, firstSyncId, lastSyncId });
        } catch (error) {
          console.log('Sync check', key, firstSyncId, lastSyncId);
          console.error('Error putting sync check', error);
        }
      }
    });

    // 5) Hydrate in‑memory models
    // IMPORTANT: Do NOT wrap this in runInAction(async () => {...}).
    // runInAction doesn't await async callbacks, so the store wouldn't be
    // populated by the time this function resolves — causing stale UI renders.
    const all = await config.dbTable.toArray();
    all.forEach((attrs: T) => config.modelStore.upsert(attrs));
  });
}

export async function hydrateEntityViaHydrationDelegator<T>(
  hydrationDelegatorConfig: HydrationDelegatorConfig<T>,
): Promise<void> {
  return trackHydration(hydrationDelegatorConfig.hydrationKey, async () => {
    await hydrationDelegatorConfig.hydrationDelegator.hydrate({
      fullHydrateEndpoint: hydrationDelegatorConfig.fullHydrateEndpoint,
      deltaSyncEndpoint: hydrationDelegatorConfig.deltaSyncEndpoint,
    });
  });
}

async function fullHydrate<Model, T>(
  config: HydrationConfig<Model, T>,
): Promise<void> {
  const syncCheckKey = config.syncKeyOverride || config.entity;
  const resp = await pdkAxios.get<FullHydrateResponse<T>>(
    config.fullHydrateEndpoint,
    {
      timeout: 60000, // 60 seconds for large hydration endpoints
    },
  );

  const { data, firstSyncId, lastSyncId } = resp.data;

  // Clear & bulk insert
  await db.transaction('rw', config.dbTable, db.syncCheck, async () => {
    try {
      await config.dbTable.clear();
    } catch (error) {
      console.log('Error clearing table', config.dbTable);
      console.error('Error clearing table', error);
    }
    try {
      await config.dbTable.bulkPut(data);
    } catch (error) {
      console.log('Bulk records', config.dbTable, data);
      console.error('Error bulk putting records', error);
    }

    try {
      await db.syncCheck.put({
        collection: syncCheckKey,
        firstSyncId,
        lastSyncId,
      });
    } catch (error) {
      console.log('Sync check', syncCheckKey, firstSyncId, lastSyncId);
      console.error('Error putting sync check', error);
    }
  });

  // Hydrate in‑memory models
  data.forEach((attrs) => config.modelStore.upsert(attrs));
}

/**
 * Hydrate hubs: sync with Dexie and populate Hub model store.
 */
export async function hydrateHubs(): Promise<void> {
  return hydrateEntityV3<Hub, HubType>(hydrationConfigs.hubs);
}

/**
 * Hydrate core deals: sync with Dexie and populate Deal model store.
 */
export async function hydrateDeals(): Promise<void> {
  await cleanupLegacyCancelledDealsFromCoreDeals();
  return hydrateEntityV3<Deal, CoreDealType>(hydrationConfigs.deals);
}

export async function hydrateCancelledDeals(): Promise<void> {
  return trackHydration(CANCELLED_DEALS_SYNC_KEY, async () => {
    const cachedCancelledDeals = await db.cancelled_core_deals.toArray();
    cachedCancelledDeals.forEach((attrs) => dealStore.upsert(attrs));

    const checkpoint = await db.syncCheck.get(CANCELLED_DEALS_SYNC_KEY);
    if (!checkpoint) {
      await fullHydrateCancelledDeals();
      return;
    }

    const url = `/hydrate/deltas/deals?sinceSyncId=${checkpoint.lastSyncId}`;
    const { entries, firstSyncId, lastSyncId } = (
      await pdkAxios.get<SyncResponse<CoreDealType>>(url, {
        timeout: 60000,
      })
    ).data;

    if (checkpoint.lastSyncId && checkpoint.lastSyncId < firstSyncId) {
      await fullHydrateCancelledDeals();
      return;
    }

    await db.transaction(
      'rw',
      db.cancelled_core_deals,
      db.core_deals,
      db.syncCheck,
      async () => {
        for (const { action, data, record_id } of entries) {
          if (action === 'delete' || action === 'archive') {
            await db.cancelled_core_deals.delete(record_id);
            await db.core_deals.delete(record_id);
            continue;
          }

          if (data.status === 'cancelled') {
            await db.cancelled_core_deals.put(data, record_id);
            continue;
          }

          await db.cancelled_core_deals.delete(record_id);
          await db.core_deals.put(data, record_id);
        }

        if (lastSyncId > (checkpoint.lastSyncId || 0)) {
          await db.syncCheck.put({
            collection: CANCELLED_DEALS_SYNC_KEY,
            firstSyncId,
            lastSyncId,
          });
        }
      },
    );

    for (const { action, data, record_id } of entries) {
      if (action === 'delete' || action === 'archive') {
        dealStore.removeById(record_id);
      } else if (data.status !== 'cancelled') {
        dealStore.upsert(data);
      }
    }

    const cancelledDeals = await db.cancelled_core_deals.toArray();
    cancelledDeals.forEach((attrs) => dealStore.upsert(attrs));
    dealStore.markCancelledDealsLoaded();
  });
}

async function fullHydrateCancelledDeals(): Promise<void> {
  const resp = await pdkAxios.get<FullHydrateResponse<CoreDealType>>(
    '/hydrate/v2/deals/cancelled',
    {
      timeout: 60000,
    },
  );
  const { data, firstSyncId, lastSyncId } = resp.data;

  await db.transaction(
    'rw',
    db.cancelled_core_deals,
    db.syncCheck,
    async () => {
      await db.cancelled_core_deals.clear();
      await db.cancelled_core_deals.bulkPut(data);
      await db.syncCheck.put({
        collection: CANCELLED_DEALS_SYNC_KEY,
        firstSyncId,
        lastSyncId,
      });
    },
  );

  data.forEach((attrs) => dealStore.upsert(attrs));
  dealStore.markCancelledDealsLoaded();
}

/**
 * Hydrate users: sync with Dexie and populate User model store.
 */
export async function hydrateUsers(): Promise<void> {
  return hydrateEntityV3<User, UserType>(hydrationConfigs.users);
}

/**
 * Hydrate commentsV3: sync with Dexie and populate CommentV3 model store.
 */
export async function hydrateComments(): Promise<void> {
  return hydrateEntityV3<Comment, CommentType>(hydrationConfigs.comments);
}

/**
 * Hydrate repliesV3: sync with Dexie and populate ReplyV3 model store.
 */
export async function hydrateReplies(): Promise<void> {
  return hydrateEntityV3<Reply, ReplyType>(hydrationConfigs.replies);
}

/**
 * Hydrate confirmations: sync with Dexie and populate Confirmation model store.
 */
export async function hydrateConfirmations(): Promise<void> {
  return hydrateEntityV3<Confirmation, ConfirmationType>(
    hydrationConfigs.confirmations,
  );
}

/**
 * Hydrate confirmationhistory: sync with Dexie and populate ConfirmationHistory model store.
 */
export async function hydrateConfirmationHistoryEntries(): Promise<void> {
  return hydrateEntityV3<ConfirmationHistory, ConfirmationHistoryType>(
    hydrationConfigs.confirmation_history_entries,
  );
}

/**
 * Hydrate payments: sync with Dexie and populate Payment model store.
 */
export async function hydratePayments(): Promise<void> {
  return hydrateEntityV3<Payment, PaymentType>(hydrationConfigs.payments);
}

/**
 * Hydrate files: sync with Dexie and populate FileMetadata model store.
 *
 */
export async function hydrateFiles(): Promise<void> {
  return hydrateEntityV3<FileMetadata, FileMetadataType>(
    hydrationConfigs.files,
  );
}

/**
 * Hydrate customers: sync with Dexie and populate PrimaryCustomer and CoRegistrant model stores.
 */
export async function hydrateCustomers(): Promise<void> {
  return hydrateEntityViaHydrationDelegator<CustomerType>(
    hydrationDelegatorConfigs.customers,
  );
}

/**
 * Hydrate customer addresses: sync with Dexie and populate CustomerAddress model store.
 */
export async function hydrateCustomerAddresses(): Promise<void> {
  return hydrateEntityV3<CustomerAddress, CustomerAddressType>(
    hydrationConfigs.customerAddresses,
  );
}

/**
 * Hydrate lenders: sync with Dexie and populate Lender model store.
 */
export async function hydrateLenders(): Promise<void> {
  return hydrateEntityV3<Lender, LenderType>(hydrationConfigs.lenders);
}

/**
 * Hydrate auctions: sync with Dexie and populate Auction model store.
 */
export async function hydrateAuctions(): Promise<void> {
  return hydrateEntityV3<Auction, AuctionType>(hydrationConfigs.auctions);
}

/**
 * Hydrate emoji reactions: fetch all emoji reactions and sync to IndexedDB and in-memory models
 */
export async function hydrateEmojiReactions(): Promise<void> {
  return hydrateEntityV3<EmojiReaction, EmojiReactionType>(
    hydrationConfigs.emojiReactions,
  );
}

export async function hydrateTaskTemplates(): Promise<void> {
  return hydrateEntityV3<TaskTemplate, TaskTemplateType>(
    hydrationConfigs.taskTemplates,
  );
}

export async function hydrateTasksV2(): Promise<void> {
  return hydrateEntityV3<TaskV2, TaskTypeV2>(hydrationConfigs.tasksV2);
}

/**
 * Hydrate tasks: sync with Dexie and populate Task model store.
 */
export async function hydrateTasks(): Promise<void> {
  return hydrateEntityV3<Task, TaskType>(hydrationConfigs.tasks);
}

/**
 * Hydrate audits: sync with Dexie and populate Audit model store.
 */
export async function hydrateAudits(): Promise<void> {
  return hydrateEntityV3<Audit, AuditType>(hydrationConfigs.audits);
}

/**
 * Hydrate notifications: sync with Dexie and populate Notification model store.
 */
export async function hydrateNotifications(): Promise<void> {
  return hydrateEntityV3<Notification, NotificationType>(
    hydrationConfigs.notifications,
  );
}

/**
 * Hydrate assigned tasks: sync with Dexie and populate AssignedTask model store.
 */
export async function hydrateAssignedTasks(): Promise<void> {
  return hydrateEntityV3<AssignedTask, AssignedTaskType>(
    hydrationConfigs.assignedTasks,
  );
}

/**
 * Hydrate protection plans: sync with Dexie and populate ProtectionPlan model store.
 */
export async function hydrateProtectionPlans(): Promise<void> {
  return hydrateEntityV3<ProtectionPlan, ProtectionPlanType>(
    hydrationConfigs.protectionPlans,
  );
}

/**
 * Hydrate price breakdowns: sync with Dexie and populate PriceBreakdown model store.
 */
export async function hydratePriceBreakdowns(): Promise<void> {
  return hydrateEntityV3<PriceBreakdown, PriceBreakdownType>(
    hydrationConfigs.priceBreakdowns,
  );
}

/**
 * Hydrate favorite deals: sync with Dexie and populate Favorite model store.
 */
export async function hydrateFavoriteDeals(): Promise<void> {
  return hydrateEntityV3<Favorite, FavoriteType>(
    hydrationConfigs.favoriteDeals,
  );
}

/**
 * Hydrate trades: sync with Dexie and populate Trade model store.
 */
export async function hydrateTrades(): Promise<void> {
  return hydrateEntityV3<Trade, TradeType>(hydrationConfigs.trades);
}

/**
 * Hydrate vehicle listings and vehicles from indexer
 */
export async function hydrateVehiclesAndListingsFromIndexer(): Promise<void> {
  return hydrateEntityViaHydrationDelegator<VehicleListingType>(
    hydrationDelegatorConfigs.vehicleListings,
  );
}

/**
 * Hydrate SMS agent messages: sync with Dexie and populate SMSAgentMessage model store.
 */
export async function hydrateSMSAgentMessages(): Promise<void> {
  return hydrateEntityV3<SMSAgentMessage, SMSAgentMessageType>(
    hydrationConfigs.smsAgentMessages,
  );
}

export async function hydrateAppraisalsV2(): Promise<void> {
  return hydrateEntityV3<AppraisalV2, AppraisalV2Type>(
    hydrationConfigs.appraisalsV2,
  );
}

export async function hydrateAppraisalOffersV2(): Promise<void> {
  return hydrateEntityV3<AppraisalOfferV2, AppraisalOfferV2Type>(
    hydrationConfigs.appraisalOffersV2,
  );
}

export async function hydrateInstantOffersV2(): Promise<void> {
  return hydrateEntityV3<InstantOfferV2, InstantOfferV2Type>(
    hydrationConfigs.instantOffersV2,
  );
}

export async function hydrateSellers(): Promise<void> {
  return hydrateEntityViaHydrationDelegator<SellerType>(
    hydrationDelegatorConfigs.sellers,
  );
}

export async function hydrateProtectionPlanNames(): Promise<void> {
  return hydrateEntityViaHydrationDelegator<ProtectionPlanNameType>(
    hydrationDelegatorConfigs.protectionPlanNames,
  );
}

export async function hydrateProtectionPlanTypes(): Promise<void> {
  return hydrateEntityViaHydrationDelegator<ProtectionPlanTypeType>(
    hydrationDelegatorConfigs.protectionPlanTypes,
  );
}

export async function hydrateProtectionPlanTermLengths(): Promise<void> {
  return hydrateEntityViaHydrationDelegator<ProtectionPlanTermLengthType>(
    hydrationDelegatorConfigs.protectionPlanTermLengths,
  );
}

export async function hydrateProtectionPlanTermMiles(): Promise<void> {
  return hydrateEntityViaHydrationDelegator<ProtectionPlanTermMilesType>(
    hydrationDelegatorConfigs.protectionPlanTermMiles,
  );
}

/**
 * Hydrate vehicle locations: sync with Dexie and populate VehicleLocation model store.
 */
export async function hydrateVehicleLocations(): Promise<void> {
  return hydrateEntityV3<VehicleLocation, VehicleLocationType>(
    hydrationConfigs.vehicleLocations,
  );
}
